/* Halloween Home panel only. Product page animations keep their own timing. */
(() => {
  const key = '__tacSpookyHome';
  if (window[key]) {
    window[key].mountAll();
    return;
  }

  const SLOT_MS = 15_000;
  const SCENE_MS = 8_000;
  const COVER_MIDPOINT_MS = 350;
  const SCENES = ['bats', 'pumpkin', 'skeleton', 'witch'];
  const mounted = new WeakMap();

  function mount(root) {
    if (mounted.has(root)) return;
    const panel = root.querySelector('.tac-spooky-home__panel');
    const layer = root.querySelector('[data-home-scenes]');
    const stack = root.querySelector('.tac-spooky-home__book-stack');
    const spooky = stack?.querySelector('[data-cover="spooky"]');
    const minimal = stack?.querySelector('[data-cover="minimal"]');
    const button = root.querySelector('.tac-spooky-home__button');
    const price = root.querySelector('[data-cover-price]');
    if (!panel || !layer || !stack || !spooky || !minimal) return;

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const canAlternate = button?.dataset.canAlternate === 'true';
    let prepared = false;
    let minimalReady = false;
    let visible = false;
    let destroyed = false;
    let elapsedMs = 0;
    let runningSince = null;
    let timer = null;
    let linkTimer = null;
    let currentCover = 'spooky';

    const syncCommercialCover = (cover) => {
      root.dataset.homeCover = cover;
      stack.setAttribute('aria-label', stack.dataset[cover === 'minimal' ? 'minimalAlt' : 'spookyAlt'] || '');
      if (button) button.href = button.dataset[cover === 'minimal' ? 'minimalHref' : 'spookyHref'];
      if (price) price.textContent = price.dataset[cover === 'minimal' ? 'minimalPrice' : 'spookyPrice'];
    };

    const setCover = (cover) => {
      if (cover === currentCover && !motion.matches) return;
      if (linkTimer !== null) clearTimeout(linkTimer);
      linkTimer = null;
      if (cover !== currentCover) {
        currentCover = cover;
        spooky.classList.toggle('is-current', cover === 'spooky');
        minimal.classList.toggle('is-current', cover === 'minimal');
      }
      if (motion.matches) {
        syncCommercialCover(cover);
        return;
      }
      linkTimer = setTimeout(() => {
        linkTimer = null;
        if (!destroyed && currentCover === cover) syncCommercialCover(cover);
      }, COVER_MIDPOINT_MS);
    };

    const pause = () => {
      if (runningSince !== null) {
        elapsedMs += performance.now() - runningSince;
        runningSince = null;
      }
      if (timer !== null) clearTimeout(timer);
      timer = null;
      layer.dataset.scene = 'idle';
    };

    const tick = () => {
      if (destroyed || runningSince === null) return;
      const activeMs = elapsedMs + performance.now() - runningSince;
      const slot = Math.floor(activeMs / SLOT_MS);
      const slotMs = activeMs - slot * SLOT_MS;
      const cover = canAlternate && minimalReady && slot % 2 === 1 ? 'minimal' : 'spooky';
      setCover(cover);

      const scene = slot > 0 && slotMs < SCENE_MS ? SCENES[(slot - 1) % SCENES.length] : 'idle';
      layer.style.setProperty('--tac-scene-offset', `${-Math.floor(slotMs)}ms`);
      layer.dataset.scene = scene;
      const untilBoundary = slot > 0 && slotMs < SCENE_MS ? SCENE_MS - slotMs : SLOT_MS - slotMs;
      timer = setTimeout(tick, Math.max(16, Math.ceil(untilBoundary) + 4));
    };

    const updateActivity = () => {
      if (destroyed) return;
      if (!prepared || !visible || document.visibilityState === 'hidden' || motion.matches) {
        pause();
        if (motion.matches) setCover('spooky');
        return;
      }
      if (runningSince !== null) return;
      runningSince = performance.now();
      tick();
    };

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
        visible = entries[0]?.isIntersecting === true;
        updateActivity();
      }, { threshold: 0.05 })
      : null;
    if (observer) observer.observe(panel);
    else visible = true;

    const onVisibility = () => updateActivity();
    const onMotion = () => updateActivity();
    document.addEventListener('visibilitychange', onVisibility);
    motion.addEventListener?.('change', onMotion);
    if (!motion.addEventListener) motion.addListener?.(onMotion);

    const controller = {
      destroy() {
        destroyed = true;
        pause();
        if (linkTimer !== null) clearTimeout(linkTimer);
        observer?.disconnect();
        document.removeEventListener('visibilitychange', onVisibility);
        motion.removeEventListener?.('change', onMotion);
        if (!motion.removeEventListener) motion.removeListener?.(onMotion);
        mounted.delete(root);
      },
    };
    mounted.set(root, controller);

    Promise.allSettled([spooky.decode(), minimal.decode()]).then((results) => {
      if (destroyed) return;
      minimalReady = canAlternate && results[1].status === 'fulfilled' && minimal.naturalWidth > 0;
      prepared = true;
      updateActivity();
    });
  }

  const findIn = (target) => {
    if (target.matches?.('[data-tac-spooky-home]')) return [target];
    return [...(target.querySelectorAll?.('[data-tac-spooky-home]') || [])];
  };
  const mountAll = () => document.querySelectorAll('[data-tac-spooky-home]').forEach(mount);
  window[key] = { mountAll };
  document.addEventListener('shopify:section:load', (event) => findIn(event.target).forEach(mount));
  document.addEventListener('shopify:section:unload', (event) => {
    findIn(event.target).forEach((root) => mounted.get(root)?.destroy());
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountAll, { once: true });
  else mountAll();
})();
